import { rupeesToPaise } from "../../../lib/format.js";
import type { BillLineItem, BillTaxMode } from "./lib/form-schema.js";
import type { BillTotals } from "./types.js";

export function computeLineSubtotalPaise(row: BillLineItem): bigint {
  const qtyMillis = parseDecimalMillis(row.qty);
  const ratePaise = rupeesToPaise(row.rate);
  const discountBps = parsePercentBps(row.discountRate);
  if (qtyMillis === null || ratePaise === null || discountBps === null) return 0n;
  const gross = (ratePaise * qtyMillis) / 1000n;
  return gross - (gross * discountBps) / 10000n;
}

export function computeLineTaxPaise(row: BillLineItem): bigint {
  const taxBasisPoints = parsePercentBps(row.taxRate);
  if (taxBasisPoints === null) return 0n;
  return (computeLineSubtotalPaise(row) * taxBasisPoints) / 10000n;
}

export function computeTotals(rows: BillLineItem[], taxMode?: BillTaxMode): BillTotals {
  return rows.reduce<BillTotals>(
    (acc, row) => {
      const subtotalPaise = computeLineSubtotalPaise(row);
      const taxPaise = taxMode === "exempt" ? 0n : computeLineTaxPaise(row);
      return {
        subtotalPaise: acc.subtotalPaise + subtotalPaise,
        taxPaise: acc.taxPaise + taxPaise,
        totalPaise: acc.totalPaise + subtotalPaise + taxPaise,
      };
    },
    { subtotalPaise: 0n, taxPaise: 0n, totalPaise: 0n },
  );
}

export function hasValidBillLine(row: BillLineItem): boolean {
  return row.description.trim() !== "" && computeLineSubtotalPaise(row) > 0n;
}

function parseDecimalMillis(value: string): bigint | null {
  const cleaned = value.trim();
  if (!/^\d+(\.\d{0,3})?$/.test(cleaned)) return null;
  const [whole = "0", fraction = ""] = cleaned.split(".");
  const result = BigInt(whole || "0") * 1000n + BigInt((fraction + "000").slice(0, 3));
  return result > 0n ? result : null;
}

function parsePercentBps(value: string): bigint | null {
  const cleaned = value.trim();
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  const [whole = "0", fraction = ""] = cleaned.split(".");
  return BigInt(whole || "0") * 100n + BigInt((fraction + "00").slice(0, 2));
}
